import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { usePymbleApi, ApiMethod } from "./usePymbleApi";

export interface PastoralTag {
	id: string;
	name: string;
	colour: string;
}

export interface PastoralNoteFile {
	name: string;
	url: string;
	type?: string;
	secure?: boolean;
	container?: string;
	InternalName?: string;
	UploadedBy?: number; // Edumate contact id of the uploader (raw)
	UploadedAt?: string; // ISO timestamp, set server-side
	UploadedByDetail?: {
		// Uploader resolved server-side (mirrors a note's AuthorDetail)
		DescShort: string;
		DescLong: string;
		_id: string;
		Subtitle?: string;
	};
}

export interface PastoralNote {
	id: string;
	student_id: string;
	student_name: string;
	student_year: string;
	student_avatar?: string;
	date: string;
	pastoral_type: string;
	pastoral_type_id?: string;
	pastoral_type_colour: string;
	author_id?: string;
	/** Edumate numeric contact id of the author (mirrors my/details SmsId). */
	created_by?: number;
	/**
	 * Server-computed authority for the current caller, from the same permission
	 * check the update/delete routes enforce. Absent on responses that don't
	 * carry it — see `canEditEntity` for the fallback chain.
	 */
	can_edit?: boolean;
	can_delete?: boolean;
	tags: PastoralTag[];
	summary: string;
	status: "active" | "resolved" | "monitoring";
	// Raw lifecycle status straight from the server. CP notes flow
	// Created → Viewed → Actioned; general/complaint notes Unresolved → Resolved.
	// `Created → Viewed` is backend-driven (set when a recipient reads the note).
	raw_status?: RawNoteStatus;
	files: PastoralNoteFile[];
	author?: string;
	sentiment?: { type?: string; intensity?: number; scale?: number; label?: string };
	comment_count?: number;
	note?: string;
}

export interface PastoralComment {
	id: string;
	author: string;
	author_id?: string;
	created_by?: number;
	can_edit?: boolean;
	can_delete?: boolean;
	date: string;
	text: string;
	confidential?: boolean | null;
	isSystem?: boolean;
	files?: PastoralNoteFile[];
}

// Raw shape returned by GET /note/feed
interface NoteFeedAbout {
	_id: string;
	DescShort: string;
	DescLong: string;
	Subtitle?: string;
	ContactType?: string;
}

interface NoteFeedFile {
	Name: string;
	Url: string;
	Type?: string;
	Secure?: boolean;
	Container?: string;
	InternalName?: string;
	UploadedBy?: number;
	UploadedAt?: string;
	UploadedByDetail?: PastoralNoteFile["UploadedByDetail"];
}

interface NoteFeedItem {
	_id: string;
	Category?: string;
	CategoryLabel?: string;
	Note?: string;
	Tags?: string[];
	Author?: string;
	AuthorDetail?: { _id?: string };
	CreatedBy?: number;
	CanEdit?: boolean;
	CanDelete?: boolean;
	CreatedAt?: string;
	Status?: "Created" | "Unresolved" | "Actioned" | "Resolved" | "Viewed";
	Sentiment?: { type?: string; intensity?: number; scale?: number; label?: string };
	About?: NoteFeedAbout;
	ActiveCommentSummary?: string | null;
	NonSystemNoteCount?: number;
	Files?: NoteFeedFile[];
}

interface NoteFeedResponse {
	notes: NoteFeedItem[];
	total: number;
	page: number;
	limit: number;
	pages: number;
	sentiment_counts?: { positive: number; negative: number };
}

export const TAG_COLOUR_MAP: Record<string, string> = {
	ChildProtection: "#ff6b6b",
	DisorderedEating: "#ffd166",
	FamilyConcerns: "#95d5b2",
	LowMood: "#ff9f43",
	SelfHarm: "#e07be0",
	Anxiety: "#74c0fc",
	Bullying: "#ff8c69",
	Attendance: "#a8dadc",
	Wellbeing: "#55efc4",
	Academic: "#74b9d4",
	Leadership: "#a29bfe",
};

export function tagColour(name: string): string {
	return TAG_COLOUR_MAP[name] ?? "#dde3f0";
}

export const PREDEFINED_TAG_NAMES = Object.keys(TAG_COLOUR_MAP);

// ── Note lifecycle status ────────────────────────────────────────────────────
// Single global status per note (not per viewer), mirroring my-pymble-portal.

export type RawNoteStatus = "Created" | "Unresolved" | "Actioned" | "Resolved" | "Viewed";

/** Short human label for a raw status (mirrors the portal chip). */
export const NOTE_STATUS_LABEL: Record<RawNoteStatus, string> = {
	Created: "New",
	Viewed: "Viewed",
	Actioned: "Actioned",
	Unresolved: "Unresolved",
	Resolved: "Resolved",
};

/** Chip colour for a raw status — Resolved/Actioned green, Created/Unresolved red, Viewed amber. */
export const NOTE_STATUS_COLOUR: Record<RawNoteStatus, string> = {
	Resolved: "#2e7d32",
	Actioned: "#2e7d32",
	Viewed: "#ff9800",
	Created: "#d32f2f",
	Unresolved: "#d32f2f",
};

/** System tag added to a CP note when it is marked Actioned. */
export const ACTIONED_CP_TAG = "ActionedCP";

/** Tag id that marks a note as child protection (drives the CP lifecycle). */
export const CHILD_PROTECTION_TAG = "ChildProtection";

function mapStatus(raw?: string): PastoralNote["status"] {
	if (raw === "Resolved") return "resolved";
	if (raw === "Actioned" || raw === "Viewed") return "monitoring";
	return "active";
}

function mapFeedItem(item: NoteFeedItem): PastoralNote {
	return {
		id: item._id,
		student_id: item.About?._id ?? "",
		student_name: item.About?.DescLong || item.About?.DescShort || "Unknown",
		student_year: item.About?.Subtitle || "",
		date: item.CreatedAt ?? "",
		pastoral_type: item.CategoryLabel || item.Category || "",
		pastoral_type_id: item.Category || "",
		pastoral_type_colour: "#74b9d4",
		tags: (item.Tags ?? []).map((t) => ({ id: t, name: t, colour: tagColour(t) })),
		summary: item.ActiveCommentSummary ?? item.Note ?? "",
		status: mapStatus(item.Status),
		raw_status: item.Status,
		files: (item.Files ?? []).map((f) => ({ name: f.Name, url: f.Url, type: f.Type, secure: f.Secure, container: f.Container, InternalName: f.InternalName, UploadedBy: f.UploadedBy, UploadedAt: f.UploadedAt, UploadedByDetail: f.UploadedByDetail })),
		author: item.Author,
		author_id: item.AuthorDetail?._id ?? undefined,
		created_by: item.CreatedBy,
		can_edit: item.CanEdit,
		can_delete: item.CanDelete,
		sentiment: item.Sentiment,
		comment_count: item.NonSystemNoteCount,
		note: item.Note,
	};
}

/** Minimal shape of the signed-in user needed to resolve authorship. */
export interface AuthorIdentity {
	_id?: string;
	contact_id?: number | string;
	SmsId?: number | string;
	Name?: string;
}

/** The authorship-bearing fields shared by notes and comments. */
type EditableEntity = Pick<PastoralNote, "can_edit" | "can_delete" | "created_by" | "author_id" | "author">;

/**
 * Whether the signed-in user may edit (and therefore delete) a note or comment.
 *
 * Prefers the server's authoritative `CanEdit`, which is computed by the same
 * permission check the update route enforces. When the response doesn't carry
 * it, fall back through the author heuristics in order of reliability:
 * the Edumate numeric contact id, then the ContactPersonal `_id`, then the
 * author's display name. Matching on `author_id` alone used to be the only
 * check, which silently hid the edit button from the real author whenever the
 * response omitted `AuthorDetail`.
 *
 * This is a UX gate only — the API enforces authorship independently.
 */
export function canEditEntity(entity?: EditableEntity | null, me?: AuthorIdentity | null): boolean {
	if (!entity || !me) return false;
	if (typeof entity.can_edit === "boolean") return entity.can_edit;

	const myContactId = me.contact_id ?? me.SmsId;
	if (entity.created_by != null && myContactId != null) {
		return String(entity.created_by) === String(myContactId);
	}
	if (entity.author_id && me._id) return entity.author_id === me._id;
	if (entity.author && me.Name) return entity.author === me.Name;
	return false;
}

/**
 * Whether the signed-in user may delete a note or comment. The server ACL set
 * at creation grants Delete to the author alone — the same role Update uses —
 * so this prefers the server's `CanDelete` and otherwise defers to the edit
 * check rather than duplicating the heuristics.
 */
export function canDeleteEntity(entity?: EditableEntity | null, me?: AuthorIdentity | null): boolean {
	if (!entity || !me) return false;
	if (typeof entity.can_delete === "boolean") return entity.can_delete;
	return canEditEntity(entity, me);
}

export interface PastoralFilters {
	limit?: number;
	page?: number;
	sort?: string;
	category?: string;
	tags?: string[];
	search?: string;
	// Filter by note author: the author's ContactPersonal _id (as returned by the
	// staff /search endpoint). Sent to /feed as `author` for an exact author
	// match; not the same as `search`, which matches note content/subjects.
	author?: string;
	sentiment_type?: "good" | "bad" | "neutral" | "compliment" | "complaint" | "note";
	status?: "Unresolved" | "Resolved" | "Created" | "Viewed" | "Actioned";
	stage?: string;
	from_date?: string;
	to_date?: string;
	// Stored as `year` in UI state, sent to API as `year_group`
	year?: string;
	// UI-only: display key for the date preset selector, not sent to API
	date_from?: string;
	// When set, the grid is scoped to a single student — fetched from the
	// context feed (note/user/context/ContactPersonal/{id}) instead of the
	// global feed. Not sent as a query param.
	student_id?: string;
}

export interface PastoralInsightsData {
	tag_usage_by_segment: {
		tag: string;
		junior: number;
		middle: number;
		upper: number;
		senior: number;
		total: number;
	}[];
	top_tags: { tag: string; count: number }[];
	common_tags: string[];
	rare_tags: { tag: string; count: number }[];
	child_protection: {
		student_id: string;
		student_name: string;
		excerpt: string;
		tags: string[];
	}[];
	watch_list: {
		student_id: string;
		student_name: string;
		student_year: string;
		student_avatar?: string;
		positive: number;
		negative: number;
	}[];
	overview: {
		category: string;
		positive: number;
		negative: number;
	}[];
}

export interface PastoralFeedMeta {
	total: number;
	page: number;
	limit: number;
	pages: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchNotes(callApi: (method: ApiMethod, url: string, options?: any) => Promise<any>, filters?: PastoralFilters) {
	const { year, date_from, student_id, ...apiParams } = filters ?? {};
	// Scope to a single student via the context feed when a student filter is
	// active; year_group is meaningless for one student so it's dropped there.
	const endpoint = student_id
		? `note/user/context/ContactPersonal/${student_id}`
		: "note/user/feed";
	const raw: NoteFeedResponse = await callApi("GET", endpoint, {
		params: { ...apiParams, ...(!student_id && year ? { year_group: year } : {}) },
	});
	return {
		notes: (raw?.notes ?? []).map(mapFeedItem),
		meta: {
			total: raw?.total ?? 0,
			page: raw?.page ?? 1,
			limit: raw?.limit ?? 20,
			pages: raw?.pages ?? 1,
		},
	};
}

export function usePastoralNotes(filters?: PastoralFilters) {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useQuery<{ notes: PastoralNote[]; meta: PastoralFeedMeta }>({
		queryKey: ["pastoralNotes", filters],
		queryFn: () => fetchNotes(callApi, filters),
		enabled: isAuthenticated,
	});
}

export function usePastoralNotesInfinite(filters?: Omit<PastoralFilters, "page">) {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useInfiniteQuery({
		queryKey: ["pastoralNotesInfinite", filters],
		queryFn: ({ pageParam }) => fetchNotes(callApi, { ...filters, page: pageParam as number }),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.meta.page < lastPage.meta.pages ? lastPage.meta.page + 1 : undefined,
		enabled: isAuthenticated,
	});
}

// Fetch a single note by id for deep-linking. The single-note endpoint
// (GET note/user/{id}) returns the raw note wrapped in { note, note_relations }
// and is not feed-enriched (no About/CategoryLabel), so student details fall
// back to the note's Context/relations. Shareable links produced in-app also
// carry ?student=, which resolves the fully-enriched note from the context
// feed first — this hook is the fallback for bare ?note= links.
export function usePastoralNote(id?: string) {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useQuery<PastoralNote | null>({
		queryKey: ["pastoralNote", id],
		queryFn: async () => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const raw: any = await callApi("GET", `note/user/${id}/`);
			const item = raw?.note ?? raw;
			if (!item?._id) return null;
			const mapped = mapFeedItem(item as NoteFeedItem);
			if (!mapped.student_id) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const relation = (raw?.note_relations ?? []).find((r: any) => r.ObjectType === "ContactPersonal");
				mapped.student_id = item.Context?.ObjectId ?? relation?.ObjectId ?? "";
			}
			return mapped;
		},
		enabled: isAuthenticated && !!id,
	});
}

export function usePastoralNotesExport() {
	const { callApi } = usePymbleApi();

	const fetchAll = async (filters: PastoralFilters, total: number): Promise<PastoralNote[]> => {
		const { year, date_from, student_id, ...apiParams } = filters;
		const endpoint = student_id
			? `note/user/context/ContactPersonal/${student_id}`
			: "note/user/feed";
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const raw: NoteFeedResponse = await callApi("GET", endpoint, {
			params: { ...apiParams, ...(!student_id && year ? { year_group: year } : {}), limit: total, page: 1 },
		});
		return (raw?.notes ?? []).map(mapFeedItem);
	};

	const fetchCommentsByNote = async (noteIds: string[]): Promise<Map<string, PastoralComment[]>> => {
		const result = new Map<string, PastoralComment[]>();
		const BATCH = 10;
		for (let i = 0; i < noteIds.length; i += BATCH) {
			const batch = noteIds.slice(i, i + BATCH);
			await Promise.all(
				batch.map(async (id) => {
					try {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						const raw = await callApi("GET", `note/user/context/Note/${id}`, {
							params: { limit: 50, sort: "CreatedAt:asc" },
						});
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						const comments: PastoralComment[] = (raw?.notes ?? []).map((n: any): PastoralComment => {
							const tags: string[] = Array.isArray(n.Tags) ? n.Tags : [];
							const isSystem =
								(n.Author ?? "").toLowerCase() === "system" ||
								tags.some((t: string) => ["system", "audit"].includes(t.toLowerCase()));
							return { id: n._id, author: n.Author ?? "Unknown", author_id: n.AuthorDetail?._id ?? undefined, created_by: n.CreatedBy, can_edit: n.CanEdit, can_delete: n.CanDelete, date: n.CreatedAt ?? "", text: n.Note ?? "", confidential: n.Confidential ?? null, isSystem, files: (n.Files ?? []).map((f: any) => ({ name: f.Name, url: f.Url, type: f.Type, secure: f.Secure, container: f.Container, InternalName: f.InternalName, UploadedBy: f.UploadedBy, UploadedAt: f.UploadedAt, UploadedByDetail: f.UploadedByDetail })) };
						});
						result.set(id, comments.filter((c) => !c.isSystem));
					} catch {
						result.set(id, []);
					}
				})
			);
		}
		return result;
	};

	return { fetchAll, fetchCommentsByNote };
}

export function usePrefetchPastoralPages(filters: PastoralFilters | undefined, currentPage: number, totalPages: number) {
	const { callApi, isAuthenticated } = usePymbleApi();
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!isAuthenticated || currentPage >= totalPages) return;
		const f = { ...filters, page: currentPage + 1 };
		queryClient.prefetchQuery({
			queryKey: ["pastoralNotes", f],
			queryFn: () => fetchNotes(callApi, f),
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, currentPage, totalPages, JSON.stringify(filters)]);
}

export function usePastoralInsights(filters?: PastoralFilters) {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useQuery<PastoralInsightsData>({
		queryKey: ["pastoralInsights", filters],
		queryFn: () => {
			const { year, date_from, ...apiParams } = filters ?? {};
			return callApi("GET", "pastoral/insights/", {
				params: { ...apiParams, ...(year ? { year_group: year } : {}) },
			});
		},
		enabled: isAuthenticated,
	});
}

export interface PastoralCategory {
	id: string;
	label: string;
}

export function usePastoralCategories(userId?: string) {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useQuery<PastoralCategory[]>({
		queryKey: ["pastoralCategories", userId],
		queryFn: async () => {
			const params: Record<string, string> = { context: "student", mode: "view" };
			if (userId) params.context_id = userId;
			const raw = await callApi("GET", "note/user/categories/", { params });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return (raw?.categories ?? []).map((c: any) => ({ id: c.value, label: c.label }));
		},
		enabled: isAuthenticated,
		staleTime: 5 * 60 * 1000,
	});
}

export function useCreatePastoralNote() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			studentId,
			noteText,
			categoryId,
			tags,
			files,
		}: {
			studentId: string;
			noteText: string;
			categoryId: string;
			tags: string[];
			files: PastoralNoteFile[];
		}) =>
			callApi("POST", "note/user/", {
				data: {
					note: {
						_id: null,
						Note: noteText,
						Category: categoryId || undefined,
						Tags: tags,
						Files: files.map((f) => ({
							Name: f.name,
							Url: f.url,
							Type: f.type,
							Secure: f.secure,
							Container: f.container,
							InternalName: f.InternalName,
							UploadedBy: f.UploadedBy,
							UploadedAt: f.UploadedAt,
						})),
						Context: { ObjectType: "ContactPersonal", ObjectId: studentId },
						Permissions: {
							Read: { Roles: ["author", "staff"] },
							Update: { Roles: ["author"] },
							Delete: { Roles: ["author"] },
						},
					},
					note_relations: [{ ObjectType: "ContactPersonal", ObjectId: studentId }],
				},
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
		},
	});
}

export function useUpdatePastoralNote() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			noteText,
			categoryId,
			tags,
		}: {
			id: string;
			noteText: string;
			categoryId?: string;
			tags?: string[];
		}) =>
			callApi("PUT", `note/user/${id}/`, {
				data: {
					note: {
						Note: noteText,
						...(categoryId && { Category: categoryId }),
						...(tags !== undefined && { Tags: tags }),
					},
				},
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
		},
	});
}

export function useUpdateNoteFiles() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, files }: { id: string; files: PastoralNoteFile[] }) =>
			callApi("PUT", `note/user/${id}/`, {
				data: {
					note: {
						Files: files.map(f => ({
							Name: f.name,
							Url: f.url,
							Type: f.type,
							Secure: f.secure,
							Container: f.container,
							InternalName: f.InternalName,
							UploadedBy: f.UploadedBy,
							UploadedAt: f.UploadedAt,
						})),
					},
				},
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
		},
	});
}

// Set a note's lifecycle status (and optionally its tags, for the CP
// Actioned flow which also toggles the ActionedCP tag). Goes through the
// same note/user/{id} PUT as other note edits. Authorship/permission is
// enforced server-side.
export function useSetNoteStatus() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, status, tags }: { id: string; status: RawNoteStatus; tags?: string[] }) =>
			callApi("PUT", `note/user/${id}/`, {
				data: {
					note: {
						Status: status,
						...(tags !== undefined && { Tags: tags }),
					},
				},
			}),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNote", variables.id] });
		},
	});
}

export function useAddNoteComment() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ noteId, text, confidential, files }: {
			noteId: string;
			text: string;
			confidential: boolean;
			files: PastoralNoteFile[];
		}) =>
			callApi("POST", "note/user/", {
				data: {
					note: {
						_id: null,
						Note: text,
						Files: files.map(f => ({
							Name: f.name,
							Url: f.url,
							Type: f.type,
							Secure: f.secure,
							Container: f.container,
							InternalName: f.InternalName,
							UploadedBy: f.UploadedBy,
							UploadedAt: f.UploadedAt,
						})),
						Context: { ObjectType: "Note", ObjectId: noteId },
						Permissions: {
							Read: { Roles: ["author", "staff"] },
							Update: { Roles: ["author"] },
							Delete: { Roles: ["author"] },
						},
						Confidential: confidential,
					},
					note_relations: [],
				},
			}),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNoteComments", variables.noteId] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
		},
	});
}

export function useDeletePastoralNote() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => callApi("DELETE", `note/user/${id}/`),
		onSuccess: (_, id) => {
			// Match the other note mutations: the grid reads from both the paged
			// and infinite feeds, and a stale single-note cache would let a deep
			// link re-open the note we just deleted.
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
			queryClient.removeQueries({ queryKey: ["pastoralNote", id] });
			queryClient.removeQueries({ queryKey: ["pastoralNoteComments", id] });
		},
	});
}

// A comment is itself a note (created via POST note/user/ with a Note context),
// so it's edited/deleted through the same note/user/{id} endpoints. These thin
// wrappers exist to invalidate the parent note's comment thread on success. The
// API enforces authorship via the comment's Update/Delete Roles: ["author"].
export function useUpdateNoteComment() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, text }: { id: string; noteId: string; text: string }) =>
			callApi("PUT", `note/user/${id}/`, {
				data: { note: { Note: text } },
			}),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNoteComments", variables.noteId] });
		},
	});
}

export function useDeleteNoteComment() {
	const { callApi } = usePymbleApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id }: { id: string; noteId: string }) =>
			callApi("DELETE", `note/user/${id}/`),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["pastoralNoteComments", variables.noteId] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotes"] });
			queryClient.invalidateQueries({ queryKey: ["pastoralNotesInfinite"] });
		},
	});
}

export function usePastoralNoteComments(noteId: string | null) {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useQuery<PastoralComment[]>({
		queryKey: ["pastoralNoteComments", noteId],
		queryFn: async () => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const raw = await callApi("GET", `note/user/context/Note/${noteId}`, {
				params: { limit: 50, sort: "CreatedAt:asc" },
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return (raw?.notes ?? []).map((n: any): PastoralComment => {
				const tags: string[] = Array.isArray(n.Tags) ? n.Tags : [];
				const isSystem =
					(n.Author ?? "").toLowerCase() === "system" ||
					tags.some((t: string) => ["system", "audit"].includes(t.toLowerCase()));
				return {
					id: n._id,
					author: n.Author ?? "Unknown",
					author_id: n.AuthorDetail?._id ?? undefined,
					created_by: n.CreatedBy,
					can_edit: n.CanEdit,
					can_delete: n.CanDelete,
					date: n.CreatedAt ?? "",
					text: n.Note ?? "",
					confidential: n.Confidential ?? null,
					isSystem,
					files: (n.Files ?? []).map((f: any) => ({
						name: f.Name,
						url: f.Url,
						type: f.Type,
						secure: f.Secure,
						container: f.Container,
						InternalName: f.InternalName,
						UploadedBy: f.UploadedBy,
						UploadedAt: f.UploadedAt,
						UploadedByDetail: f.UploadedByDetail,
					})),
				};
			});
		},
		enabled: isAuthenticated && !!noteId,
	});
}
